import type { Mutation, MutationCache } from '@tanstack/react-query'
import { getGlobalToast } from '@/components/layout/toastUtils'
import { reportError } from '@/lib/errorReporter'
import { ApiError } from '@/services/api'
import { SessionChangedError } from '@/services/api/SessionChangedError'
import { StaleReplayError } from '@/services/api/StaleReplayError'
import { useConflictStore } from '@/store/conflictStore'

type AnyMutation = Mutation<unknown, unknown, unknown, unknown>

/** 409 Conflict — route to conflict resolution. Returns true when handled. */
function routeConflict(error: unknown, mutation: AnyMutation): boolean {
  if (!(error instanceof ApiError) || error.status !== 409 || !mutation.options.mutationKey) return false

  useConflictStore.getState().addConflict({
    id: `${mutation.mutationId}-${Date.now()}`,
    mutationKey: mutation.options.mutationKey,
    variables: mutation.state.variables,
    serverData: error.data,
    errorMessage: error.message,
  })
  return true
}

/**
 * 404 on a replayed/queued mutation — the row is gone, discard the stale
 * change. Interactive 404s (e.g. an online double-delete) fall through to the
 * normal error handling. Returns true when handled.
 */
function discardStaleQueued(
  error: unknown,
  mutation: AnyMutation,
  cache: MutationCache,
  queuedMutations: WeakSet<object>,
): boolean {
  if (!(error instanceof ApiError) || error.status !== 404 || !queuedMutations.has(mutation)) return false

  cache.remove(mutation)
  getGlobalToast()?.('A queued change was discarded (item no longer exists).', 'error')
  return true
}

/**
 * A queued write too old to re-send safely (see `lib/mutationReplay.ts`) —
 * drop it rather than leave a Retry that would hit the same guard. Returns
 * true when handled.
 */
function discardStaleReplay(error: unknown, mutation: AnyMutation, cache: MutationCache): boolean {
  if (!(error instanceof StaleReplayError)) return false

  cache.remove(mutation)
  getGlobalToast()?.(error.message, 'error')
  return true
}

/**
 * A mutation whose form surfaces certain statuses inline (e.g. a 422
 * validation error mapped to a field) opts out of the global toast/report
 * for exactly those statuses via `meta.inlineErrorStatuses`. Without this,
 * an expected user-validation failure would double-surface (inline AND a
 * toast) and be logged to the error reporter as if it were an app bug
 * (#204). Genuine failures (500, network) still fall through to surface
 * here.
 */
function isHandledInline(error: unknown, mutation: AnyMutation): boolean {
  if (!(error instanceof ApiError)) return false
  const inlineStatuses = mutation.options.meta?.inlineErrorStatuses as number[] | undefined
  return inlineStatuses?.includes(error.status) ?? false
}

function surfaceError(error: unknown) {
  reportError(error)
  const showToast = getGlobalToast()
  if (showToast) {
    showToast(error instanceof Error ? error.message : 'An error occurred', 'error')
  }
}

/**
 * Builds the MutationCache `onError` handler. `getCache` is a thunk because the
 * handler is created while the cache itself is being constructed.
 */
export function createMutationErrorHandler(getCache: () => MutationCache, queuedMutations: WeakSet<object>) {
  return (error: unknown, _variables: unknown, _context: unknown, mutation: AnyMutation) => {
    // Don't toast for paused mutations — they'll retry when online
    if (mutation.state.isPaused) return
    // Abandoned because the user signed out meanwhile — nothing to report.
    if (error instanceof SessionChangedError) return
    if (routeConflict(error, mutation)) return
    if (discardStaleQueued(error, mutation, getCache(), queuedMutations)) return
    if (discardStaleReplay(error, mutation, getCache())) return
    if (isHandledInline(error, mutation)) return

    surfaceError(error)
  }
}
